/* eslint-disable jsx-a11y/control-has-associated-label */

import { LoadingSpinner } from '@/components/ui/loading/spinner';
import { UserMessage } from '@/components/ui/user-message';
import { useSubscriptionPlans } from '@/hooks/use-subscription-plans';
import { fluentFeatures } from '@/lib/constants/fluent-features';
import { tv } from '@/utils/styles';
import { getPlanPrice, getPlanPriceString } from '@/utils/subscriptions';

const PlanListingExtended = () => {
  const { isLoading, plans, error } = useSubscriptionPlans();

  if (error) return <UserMessage variant="error" heading={error} />;
  if (isLoading || !plans.length) return <LoadingSpinner isVisible />;

  const numberOfPlans = plans.length;

  const { overflow, table, tr, th, td, category } = styles();

  return (
    <div className={overflow()}>
      <table className={table()}>
        <thead>
          <tr className={tr()}>
            <th className={th()}>&nbsp;</th>
            {plans.map((plan) => {
              return (
                <th key={`title--${plan.id}`} className={th()}>
                  {plan.name}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr className={tr()}>
            <td className={td()}>&nbsp;</td>
            {plans.map((plan) => {
              const price = getPlanPrice(plan);
              return (
                <td key={`names--${plan.id}`} className={td()}>
                  {price && getPlanPriceString(price)} / {price?.interval}
                </td>
              );
            })}
          </tr>
          <tr className={tr({ className: '[&_td]:bg-transparent' })}>
            <td className={td()}>
              <span className={category()}>Usage</span>
            </td>
            {Array.from({ length: numberOfPlans }).map((_, i) => (
              <td key={`names--${i}`} className={td()}>
                &nbsp;
              </td>
            ))}
          </tr>

          <tr className={tr()}>
            <td className={td()}>Users</td>
            {plans.map((plan) => {
              return (
                <td key={`users--${plan.id}`} className={td()}>
                  {plan.entitlements?.memberLimit}
                </td>
              );
            })}
          </tr>

          <tr className={tr()}>
            <td className={td()}>Route searches</td>
            {plans.map((plan) => {
              return (
                <td key={`routes--${plan.id}`} className={td()}>
                  {plan.entitlements?.routeSearches} / month
                </td>
              );
            })}
          </tr>

          <tr className={tr()}>
            <td className={td()}>Schedule searches</td>
            {plans.map((plan) => {
              return (
                <td key={`schedules--${plan.id}`} className={td()}>
                  {plan.entitlements?.scheduleSearches} / month
                </td>
              );
            })}
          </tr>

          <tr className={tr()}>
            <td className={td()}>Free shipments included</td>
            {plans.map((plan) => {
              const field = plan.entitlements?.freeShipmentsPerMonth || 0;
              return (
                <td key={`shipping--${plan.id}`} className={td()}>
                  {field > 0 ? `${field} free / month` : 'None'}
                </td>
              );
            })}
          </tr>

          <tr className={tr()}>
            <td className={td()}>&nbsp;</td>
            <td className={td({ className: 'px-4' })} colSpan={5}>
              <small className="italic text-grey-600">
                Note: The above represents free shipments included in your plan. Additional shipment tokens can be
                purchased as needed. Up to 1500 at a time.
              </small>
            </td>
          </tr>

          {Object.keys(fluentFeatures).map((productKey) => {
            const product = fluentFeatures[productKey];

            if (product.hidden) return null;

            return (
              <>
                <tr className={tr({ className: '[&_td]:bg-transparent' })}>
                  <td className={td()}>
                    <span className={category()}>{product.name}</span>
                  </td>
                  {Array.from({ length: numberOfPlans }).map((_, i) => (
                    <td key={`product--${i}`} className={td()}>
                      &nbsp;
                    </td>
                  ))}
                </tr>
                {product.features.map((feature: string) => {
                  return (
                    <tr className={tr()} key={`features--${feature}`}>
                      <td className={td()}>{feature}</td>
                      {plans.map((plan) => {
                        const isIncluded = Object.hasOwn(plan.entitlements || {}, productKey);
                        return (
                          <td key={`features--${plan.id}`} className={td({ className: 'text-green-500' })}>
                            {isIncluded ? '✓' : ''}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {(product.futureFeatures || []).map((feature: string) => {
                  return (
                    <tr className={tr()} key={`futureFeatures--${feature}`}>
                      <td className={td()}>{feature}</td>
                      {plans.map((plan) => {
                        const isIncluded = Object.hasOwn(plan.entitlements || {}, productKey);
                        return (
                          <td key={`futureFeatures--${plan.id}`} className={td()}>
                            <small>{isIncluded ? 'Coming soon' : ''}</small>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const styles = tv({
  slots: {
    overflow: 'relative -mx-4 my-0 overflow-auto pl-4 md:mx-none md:my-none md:pl-none',
    table: 'w-full min-w-[650px] border-collapse',
    tr: '[&:last-child>td:first-child]:border-b-0 [&:last-child_td]:px-3 [&:last-child_td]:py-4 [&>:first-child]:border-t-0 [&>:first-child]:bg-transparent [&>:first-child]:pr-2 [&>:first-child]:text-left',
    th: 'border-b border-r border-t border-grey-200 bg-white py-3 text-center text-md font-medium',
    td: 'w-[16%] border-b border-r border-grey-200 bg-white py-2 text-center text-base',
    category: 'block pt-4 text-base font-semibold text-lightBlue-600',
  },
});

export { PlanListingExtended };
